import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from 'carbon-react/lib/components/button';
import Typography from 'carbon-react/lib/components/typography';

import { createProviderConnection, getCallbackUrl } from '@futrli/organisations';
import { getOnboardingStartUrl, getProductUrl, PREDICT_PRODUCT_SLUG } from '@futrli/shared-services';
import { CreateOrganisationUploadDrawer } from '@futrli/shared-services/excel-upload/excel-modals/upload/create-organisation-upload-drawer';

import { useSfaOnboardingContext } from '../../contexts/sfa-onboarding-context';
import { setupHoldingOrgWithNewDatasource } from '../../../workspaces/api';
import { SFA_FATAL_ERROR } from '../../sfa-types';
import { SfaConnectionProvider } from '../../components/sfa/sfa-connection-provider';
import { SfaContentForm } from '../../components/sfa/sfa-content-form';
import { SfaCreateNewOrgErrorMessage } from '../../components/sfa/error-messages/sfa-create-new-org-error-message';
import { SfaLoading } from '../../components/sfa/sfa-loading';
import { useMarkSfaUserSetupComplete } from '../../hooks/use-mark-sfa-user-setup-complete';

export const SfaChooseConnectionTypeContainer = () => {
  const intl = useIntl();

  const { businessId, currentUser, integrations, organisation, setError, workspace } = useSfaOnboardingContext();
  const { markSfaUserSetupComplete } = useMarkSfaUserSetupComplete();
  const [isLoading, setIsLoading] = useState<string>();
  const [lastError, setLastError] = useState<string>();
  const [isOpenExcelSidebar, setIsOpenExcelSidebar] = useState(false);

  const [isOnboarding] = useState(!currentUser?.setupCompleted);

  const text = useMemo(
    () => ({
      cancel: intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' }),
      connect: intl.formatMessage({ id: 'common.connect', defaultMessage: 'Connect' }),
      creatingNewOrg: intl.formatMessage({
        id: 'onboarding.creating_new_org',
        defaultMessage: 'Creating new organisation...'
      }),
      next: intl.formatMessage({ id: 'common.next', defaultMessage: 'Next' }),
      title: intl.formatMessage({
        id: 'onboarding.lets_connect_your_accounting_software',
        defaultMessage: "Let's connect your accounting software"
      }),
      subtitle: intl.formatMessage({
        id: 'onboarding.select_an_option_below_to_connect',
        defaultMessage: "Select an option below to connect. You'll be taken to authorise and brought back."
      })
    }),
    [intl]
  );

  const handleCancel = () => {
    setError(SFA_FATAL_ERROR.CANCEL_BY_USER);
  };

  const handleCloseExcelSidebar = () => setIsOpenExcelSidebar(false);

  const handleConnect = async (integrationId: string) => {
    if (businessId && workspace && currentUser) {
      const integration = integrations.find(i => i.id === integrationId)!;
      if (integration.slug === 'excel') {
        setIsOpenExcelSidebar(true);
        return;
      }

      setIsLoading(integrationId);
      try {
        const orgWithDs = await setupHoldingOrgWithNewDatasource(workspace!.id, organisation!.id, integrationId);
        const connectionData = await createProviderConnection(orgWithDs.workspace_id, orgWithDs.id, {
          workspace_id: orgWithDs.workspace_id,
          organisation_id: orgWithDs.id,
          datasource_id: orgWithDs.datasource.id,
          integration: integration.slug,
          user_id: currentUser.sub,
          provider: integration.slug === 'xero' ? 'xero' : 'ledgerflow',
          success_url: currentUser.setupCompleted
            ? getCallbackUrl(getProductUrl(orgWithDs.workspace_id, orgWithDs.id, PREDICT_PRODUCT_SLUG))
            : getCallbackUrl(getOnboardingStartUrl())
        });
        await markSfaUserSetupComplete(workspace.name, businessId, currentUser.attributes.email);
        window.location.href = connectionData.initiation_url; // redirect to provider authorisation page
      } catch (e) {
        const isNewError = lastError !== integrationId;
        if (isNewError) {
          setLastError(integrationId);
          setIsLoading(undefined);
        } else {
          setError(SFA_FATAL_ERROR.ERROR_CREATING_LINK);
        }
      }
    }
  };

  if (isLoading) {
    return <SfaLoading text={text.creatingNewOrg} />;
  }

  return (
    <>
      <SfaContentForm
        saveButton={
          <Button buttonType="tertiary" type="button" onClick={handleCancel}>
            {text.cancel}
          </Button>
        }
      >
        <div style={{ width: '560px', margin: '0 auto' }}>
          {lastError && <SfaCreateNewOrgErrorMessage />}

          <Typography mb={1} variant="h1">
            {text.title}
          </Typography>
          <Typography mb={3}>{text.subtitle}</Typography>

          <ul>
            {integrations.map(integration => (
              <li key={integration.id}>
                <SfaConnectionProvider
                  icon={<img src={integration.image} alt={integration.name} width="48" height="48" />}
                  name={integration.name}
                  onConnect={() => handleConnect(integration.id)}
                  connectTextOverride={
                    integration.slug === 'excel' ? (
                      <FormattedMessage id="predict.upload_excel" defaultMessage="Upload Excel" />
                    ) : undefined
                  }
                />
              </li>
            ))}
          </ul>
        </div>
      </SfaContentForm>
      <CreateOrganisationUploadDrawer
        isOpen={isOpenExcelSidebar}
        onClose={handleCloseExcelSidebar}
        workspaceId={workspace?.id || ''}
        integration={integrations.find(i => i.slug === 'excel')}
        isOnboarding={isOnboarding}
        workspaceByBusinessId={workspace}
        organisationByBusinessId={organisation}
        businessId={businessId}
      />
    </>
  );
};
